import React from 'react';
import Card from '../components/Card';

// Shared presentational primitives for in-app documentation pages (doc-277).
// Docs are authored as JSX — there is no markdown pipeline and no
// dangerouslySetInnerHTML — so these are the house-style building blocks every
// doc uses. Tokens only: dark mode comes free, and nothing here hardcodes a
// --color-* literal.

// A doc body is prose. The app's content column is up to 1240px wide, which is
// far past a comfortable measure, so the doc page caps itself here.
export const DOC_MAX_WIDTH = 820;

export function DocSection({ title, icon, children }) {
  return (
    <Card title={title} icon={icon} style={{ marginBottom: 16 }}>
      {children}
    </Card>
  );
}

export function DocP({ children, style }) {
  return (
    <p
      style={{
        margin: '0 0 12px',
        fontSize: 'var(--font-size-base)',
        lineHeight: 1.65,
        color: 'var(--text-secondary)',
        ...style,
      }}
    >
      {children}
    </p>
  );
}

// Sub-heading inside a section, for docs long enough that one Card holds
// several ideas (the endpoint list, for instance).
export function DocH({ children, style }) {
  return (
    <div
      style={{
        margin: '18px 0 8px',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--text-primary)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function DocList({ children }) {
  return (
    <ul
      style={{
        margin: '0 0 12px',
        paddingLeft: 20,
        fontSize: 'var(--font-size-base)',
        lineHeight: 1.65,
        color: 'var(--text-secondary)',
      }}
    >
      {children}
    </ul>
  );
}

export function DocLi({ children }) {
  return <li style={{ marginBottom: 6 }}>{children}</li>;
}

// Inline monospace for a path, param, or literal value.
//
// overflow-wrap, NOT word-break (doc-318). They read as synonyms and are not:
// `word-break: break-word` behaves like `overflow-wrap: anywhere`, which drops
// the element's MIN-CONTENT width to about one character. Inside a DocTable —
// table-layout auto, width 100% — that lets the prose column take everything
// and starves the chip column, so a short single-token chip gets broken
// mid-token and, because Code draws its own border and surface, renders as two
// separate stacked chips. `overflow-wrap: break-word` only breaks a word that
// would otherwise overflow its line and leaves min-content intact, so the long
// space-separated chips still wrap instead of overflowing but the column can no
// longer be squeezed below one token. See styles/docCodeChipWrap.test.js.
export function Code({ children }) {
  return (
    <code
      className="mono"
      style={{
        fontSize: 12.5,
        padding: '1px 5px',
        background: 'var(--surface-secondary)',
        border: '0.5px solid var(--border-primary)',
        borderRadius: 4,
        color: 'var(--text-primary)',
        overflowWrap: 'break-word',
      }}
    >
      {children}
    </code>
  );
}

// Copy-paste block. Same surface/border/radius treatment as the key field in
// features/admin/ApiKeysPanel.js, so both read as one system in both themes.
export function CodeBlock({ children, label }) {
  return (
    <div style={{ margin: '0 0 14px' }}>
      {label && (
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-tertiary)',
            marginBottom: 6,
          }}
        >
          {label}
        </div>
      )}
      <pre
        className="mono"
        style={{
          margin: 0,
          padding: '10px 12px',
          fontSize: 12.5,
          lineHeight: 1.6,
          background: 'var(--surface-secondary)',
          border: '0.5px solid var(--border-primary)',
          borderRadius: 6,
          color: 'var(--text-primary)',
          // pre-wrap, not pre: the curl samples already use `\` continuations,
          // so a long line that still overflows should WRAP rather than force
          // horizontal scrolling to read it. userSelect keeps copy-paste exact.
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          userSelect: 'all',
        }}
      >
        {children}
      </pre>
    </div>
  );
}

// Small reference table. Deliberately not `.tbl`: that recipe styles rows as
// clickable (pointer cursor + hover fill), which lies about static reference
// rows.
export function DocTable({ head, rows }) {
  return (
    <div style={{ overflowX: 'auto', margin: '0 0 14px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
        <thead>
          <tr>
            {head.map((cell) => (
              <th
                key={cell}
                style={{
                  textAlign: 'left',
                  padding: '6px 10px 6px 0',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text-tertiary)',
                  borderBottom: '0.5px solid var(--border-primary)',
                  whiteSpace: 'nowrap',
                }}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: '8px 10px 8px 0',
                    verticalAlign: 'top',
                    color: j === 0 ? 'var(--text-primary)' : 'var(--text-secondary)',
                    borderBottom: '0.5px solid var(--border-primary)',
                    lineHeight: 1.55,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
